/**
 * Maps our chat read model onto the shape opencode's transcript components consume.
 *
 * The two models disagree in three ways, and all three are resolved here rather than
 * inside the ported components — that is what keeps those components 1:1 with upstream
 * and re-syncable when `repos/opencode` moves.
 *
 *   1. Discriminant. Ours is `kind`, upstream's is `type`.
 *   2. Tool state. Ours is a flat `status` plus `args`/`resultText`; upstream nests
 *      `state: { status, input, output }`. Our `awaiting_approval` and `denied` have no
 *      upstream analogue and are carried through as extra statuses so approval stays
 *      inside upstream's tool wrapper instead of getting a container of its own.
 *   3. Tool names. Ours are prefixed (`workspace_read`); upstream keys its rich per-tool
 *      presentations on unprefixed names (`read`). Without the map every tool would fall
 *      back to the generic card.
 */

import type { ChatPart, ChatToolPart, ChatToolStatus } from "@workerstation/shared";

/** Upstream's statuses, plus the two of ours that upstream has no equivalent for. */
export type TranscriptToolStatus =
  | "streaming"
  | "running"
  | "completed"
  | "error"
  | "awaiting_approval"
  | "denied";

export interface TranscriptToolState {
  readonly status: TranscriptToolStatus;
  readonly input: Record<string, unknown>;
  readonly output?: string;
  readonly title?: string;
  readonly error?: string;
  readonly metadata?: Record<string, unknown>;
}

export interface TranscriptTextPart {
  readonly type: "text";
  readonly id: string;
  readonly text: string;
}

export interface TranscriptReasoningPart {
  readonly type: "reasoning";
  readonly id: string;
  readonly text: string;
}

export interface TranscriptToolPart {
  readonly type: "tool";
  readonly id: string;
  readonly tool: string;
  /** The tool name before mapping, for callers that need to act on the real tool. */
  readonly sourceTool: string;
  readonly state: TranscriptToolState;
  readonly autoApproved?: boolean;
  readonly approvalId?: string;
  readonly destructive?: boolean;
}

/** Ours; upstream has no proposal concept, so it keeps its own presentation. */
export interface TranscriptProposalPart {
  readonly type: "proposal";
  readonly id: string;
  readonly part: Extract<ChatPart, { kind: "proposal" }>;
}

export type TranscriptPart =
  | TranscriptTextPart
  | TranscriptReasoningPart
  | TranscriptToolPart
  | TranscriptProposalPart;

/**
 * Our tool names to upstream's. An unmapped name is passed through unchanged, which lands
 * on upstream's generic tool card — the correct fallback, and why this map can grow
 * without a migration.
 */
export const TOOL_NAME_MAP: Readonly<Record<string, string>> = {
  workspace_read: "read",
  workspace_search: "grep",
  workspace_apply_patch: "apply_patch",
  workspace_create: "write"
};

export function mapToolName(tool: string): string {
  return TOOL_NAME_MAP[tool] ?? tool;
}

/**
 * Our status to upstream's. `awaiting_approval` and `denied` pass through: they are real
 * states of our agent that upstream never has, and collapsing them onto `running` or
 * `error` would hide the approval buttons or misreport a refusal as a failure.
 */
export function mapToolStatus(status: ChatToolStatus): TranscriptToolStatus {
  switch (status) {
    case "running":
      return "running";
    case "completed":
      return "completed";
    case "error":
      return "error";
    case "awaiting_approval":
      return "awaiting_approval";
    case "denied":
      return "denied";
  }
}

export interface PatchFileEntry {
  readonly filePath: string;
  readonly relativePath: string;
  readonly type: "add" | "update" | "delete" | "move";
  readonly before?: string;
  readonly after?: string;
  readonly movePath?: string;
}

/**
 * Turns `workspace_apply_patch` arguments into the per-file entries upstream's apply-patch
 * view expects.
 *
 * `writes` gives the new content; the prior content comes from `baseline`, which the
 * workbench already keeps to compute its own changes view. A write with no baseline entry
 * is a new file; a write whose baseline is unknown (no baseline passed at all) is emitted
 * without `before`, which upstream renders as an add. That is a visible degradation, not a
 * silent one, and still better than the generic card.
 */
export function patchFilesFromArgs(
  args: Record<string, unknown>,
  baseline?: Readonly<Record<string, string>>
): PatchFileEntry[] {
  const files: PatchFileEntry[] = [];

  const writes = args.writes;
  if (writes && typeof writes === "object" && !Array.isArray(writes)) {
    for (const [filePath, content] of Object.entries(writes as Record<string, unknown>)) {
      if (typeof content !== "string") continue;
      const before = baseline?.[filePath];
      files.push({
        filePath,
        relativePath: filePath,
        type: before === undefined ? "add" : "update",
        ...(before === undefined ? {} : { before }),
        after: content
      });
    }
  }

  const deletes = args.deletes;
  if (Array.isArray(deletes)) {
    for (const filePath of deletes) {
      if (typeof filePath !== "string") continue;
      const before = baseline?.[filePath];
      files.push({
        filePath,
        relativePath: filePath,
        type: "delete",
        ...(before === undefined ? {} : { before })
      });
    }
  }

  const renames = args.renames;
  if (Array.isArray(renames)) {
    for (const rename of renames) {
      if (!rename || typeof rename !== "object") continue;
      const { from, to } = rename as { from?: unknown; to?: unknown };
      if (typeof from !== "string" || typeof to !== "string") continue;
      const before = baseline?.[from];
      files.push({
        filePath: from,
        relativePath: from,
        type: "move",
        movePath: to,
        ...(before === undefined ? {} : { before, after: before })
      });
    }
  }

  return files;
}

function toolPart(
  part: ChatToolPart,
  index: number,
  baseline?: Readonly<Record<string, string>>
): TranscriptToolPart {
  const tool = mapToolName(part.tool);
  const input: Record<string, unknown> = { ...part.args };

  // Upstream's apply-patch view reads `input.files`; ours arrive as writes/deletes/renames.
  if (tool === "apply_patch") {
    input.files = patchFilesFromArgs(part.args, baseline);
  }

  const status = mapToolStatus(part.status);
  return {
    type: "tool",
    id: part.callId || `tool-${index}`,
    tool,
    sourceTool: part.tool,
    state: {
      status,
      input,
      ...(part.resultText === undefined
        ? {}
        : status === "error"
          ? { error: part.resultText }
          : { output: part.resultText })
    },
    ...(part.autoApproved ? { autoApproved: true } : {}),
    ...(part.approvalId === undefined ? {} : { approvalId: part.approvalId }),
    ...(part.destructive === undefined ? {} : { destructive: part.destructive })
  };
}

/** One part, adapted. `index` only supplies a stable id when the part carries none. */
export function toTranscriptPart(
  part: ChatPart,
  index: number,
  baseline?: Readonly<Record<string, string>>
): TranscriptPart {
  switch (part.kind) {
    case "text":
      return { type: "text", id: `text-${index}`, text: part.text };
    case "reasoning":
      return { type: "reasoning", id: `reasoning-${index}`, text: part.text };
    case "tool":
      return toolPart(part, index, baseline);
    case "proposal":
      return { type: "proposal", id: part.proposalId, part };
  }
}

export function toTranscriptParts(
  parts: readonly ChatPart[],
  baseline?: Readonly<Record<string, string>>
): TranscriptPart[] {
  return parts.map((part, index) => toTranscriptPart(part, index, baseline));
}

/**
 * Whether a part is worth rendering. Upstream drops empty text and reasoning so a turn
 * that produced only whitespace leaves no gap in the transcript.
 */
export function renderable(part: TranscriptPart): boolean {
  if (part.type === "text" || part.type === "reasoning") return part.text.trim().length > 0;
  return true;
}
